import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ImagePlus } from 'lucide-react';
import { BULK_MAX_ROWS, MAX_UPLOAD_BYTES } from '@designdeck/shared';
import { Button } from '@/components/ui';
import { addFilesToRows } from '@/lib/bulk/pairing';
import { cn } from '@/lib/utils';
import {
  useBulkCreationStore,
  type BulkZone,
} from '@/stores/bulkCreationStore';
import type { MotifUpload } from './useMotifUploads';

/** Formats a bulk run takes (R14); the API renders SVG to PNG on arrival. */
export const BULK_MOTIF_TYPES = [
  'image/png',
  'image/jpeg',
  'image/svg+xml',
] as const;

/** Some systems hand over SVGs without a MIME type; the extension decides then. */
const BULK_MOTIF_EXTENSIONS = /\.(png|jpe?g|svg)$/i;

const MAX_UPLOAD_MB = Math.round(MAX_UPLOAD_BYTES / 1024 / 1024);

type FileCheck = 'ok' | 'type' | 'size';

function checkFile(file: File): FileCheck {
  const type = file.type.toLowerCase();
  const typeOk =
    type === ''
      ? BULK_MOTIF_EXTENSIONS.test(file.name)
      : (BULK_MOTIF_TYPES as readonly string[]).includes(type);
  if (!typeOk) return 'type';
  if (file.size > MAX_UPLOAD_BYTES) return 'size';
  return 'ok';
}

function joinNames(files: readonly File[]): string {
  return files.map((file) => file.name).join(', ');
}

export interface UploadZoneProps {
  zone: BulkZone;
  /** The zone takes no files (back zone without any active product with a back). */
  disabled?: boolean;
  /** Receives the cells the drop created, to start their uploads. */
  onUploadsAdded: (uploads: MotifUpload[]) => void;
  className?: string;
}

/**
 * One upload zone of step 02 (R14–R16): drag & drop or file picker for
 * several files. Rejects other formats and oversized files before anything
 * is uploaded, refuses a drop that would push the table past
 * `BULK_MAX_ROWS` as a whole, and reports duplicates the store turned away.
 */
export function UploadZone({
  zone,
  disabled = false,
  onUploadsAdded,
  className,
}: UploadZoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const titleId = useId();
  const hintId = useId();
  const browseId = useId();

  const handleFiles = (fileList: FileList | null) => {
    if (disabled || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    const wrongType = files.filter((file) => checkFile(file) === 'type');
    const tooLarge = files.filter((file) => checkFile(file) === 'size');
    const accepted = files.filter((file) => checkFile(file) === 'ok');

    if (wrongType.length > 0) {
      toast.error(
        t('seller.bulk.motifs.toasts.fileTypeRejected', {
          fileName: joinNames(wrongType),
        })
      );
    }
    if (tooLarge.length > 0) {
      toast.error(
        t('seller.bulk.motifs.toasts.fileTooLarge', {
          fileName: joinNames(tooLarge),
          size: MAX_UPLOAD_MB,
        })
      );
    }
    if (accepted.length === 0) return;

    const store = useBulkCreationStore.getState();
    // Dry run on the pure row model: a drop past the cap adds nothing, so the
    // seller never ends up with an arbitrary cut through the sorted files.
    if (
      addFilesToRows(store.rows, zone, accepted).rows.length > BULK_MAX_ROWS
    ) {
      toast.error(
        t('seller.bulk.motifs.toasts.tooManyRows', { limit: BULK_MAX_ROWS })
      );
      return;
    }

    const result = store.addFiles(zone, accepted);
    if (result.duplicates.length > 0) {
      toast.error(
        t('seller.bulk.motifs.toasts.duplicate', {
          fileName: joinNames(result.duplicates),
        })
      );
    }
    if (result.added.length > 0) {
      onUploadsAdded(
        result.added.map(({ cellId, file }) => ({ cellId, file }))
      );
    }
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    handleFiles(event.dataTransfer.files);
  };

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    // Allow picking the same file again after removing it.
    event.target.value = '';
  };

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      aria-describedby={hintId}
      aria-disabled={disabled || undefined}
      data-testid={`bulk-upload-zone-${zone}`}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={onDrop}
      className={cn(
        'flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed p-6 text-center',
        'transition-[background-color,border-color] duration-[120ms]',
        disabled
          ? 'cursor-not-allowed border-line bg-paper-2 opacity-60'
          : isDragOver
            ? 'border-primary bg-primary-soft'
            : 'border-line bg-paper',
        className
      )}
    >
      <span
        aria-hidden="true"
        className="grid h-11 w-11 place-items-center rounded-full bg-paper-3 text-ink-2"
      >
        <ImagePlus className="h-5 w-5" />
      </span>
      <h3 id={titleId} className="dc-h3 text-base text-ink">
        {t(`seller.bulk.motifs.zones.${zone}`)}
      </h3>
      <p id={hintId} className="max-w-xs text-sm text-ink-2">
        {disabled
          ? t('seller.bulk.motifs.zones.backDisabled')
          : t('seller.bulk.motifs.zones.dropHint')}
      </p>
      <Button
        id={browseId}
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-labelledby={`${browseId} ${titleId}`}
        onClick={() => inputRef.current?.click()}
      >
        {t('seller.bulk.motifs.zones.browse')}
      </Button>
      <p className="font-mono text-[12.5px] text-mute">
        {t('seller.bulk.motifs.zones.accepted', { size: MAX_UPLOAD_MB })}
      </p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={BULK_MOTIF_TYPES.join(',')}
        disabled={disabled}
        onChange={onSelect}
        data-testid={`bulk-upload-input-${zone}`}
        className="hidden"
        tabIndex={-1}
      />
    </div>
  );
}
